import { motion } from "framer-motion";
import { X, Zap, Shield, AlertTriangle, Target, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getVisuals, TREND_ICON, CONF_ICON } from "./treemap-utils";
import { Minus } from "lucide-react";

export function TileDetail({ node, onClose }) {
  if (!node) return null;
  const v = getVisuals(node.state, node.urgency);
  const TIcon = TREND_ICON[node.trend] || Minus;
  const BoundaryIcon = node.boundary_status === "exceeding" ? AlertTriangle : Shield;
  const bd = node.biggest_driver;
  const crossImpacts = node.cross_impacts || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}
      data-testid="tile-detail-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-2xl rounded-2xl border border-border/50 p-6 md:p-8 max-h-[85vh] overflow-y-auto"
        style={{ background: "hsl(120 10% 4%)" }} data-testid="tile-detail-content">

        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={`${v.badge} border text-xs`}>{v.label}</Badge>
              <Badge variant="outline" className="text-xs border-white/10 text-white/50">
                <BoundaryIcon className="w-3 h-3 mr-1" />
                {node.boundary_status === "within" ? "Within limits" : node.boundary_status === "approaching" ? "Approaching limit" : "Exceeding limit"}
              </Badge>
              {node.trend_duration && (
                <span className="text-[10px] text-white/30">{node.trend_duration}</span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>{node.title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-detail">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex items-end gap-4 mb-6">
          <span className="text-5xl font-bold" style={{ color: v.accent, fontFamily: "Manrope, sans-serif" }}>{node.metric}</span>
          <div className="flex items-center gap-1.5 pb-2 text-white/40">
            <TIcon className="w-4 h-4" />
            <span className="text-sm">{node.metric_context}</span>
          </div>
        </div>

        {bd && (
          <div className="mb-6 rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${v.accent}08, ${v.accent}03)`, border: `1px solid ${v.border}` }}
            data-testid="biggest-driver">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4" style={{ color: v.accent }} />
              <span className="text-xs font-medium uppercase tracking-wider text-white/40">Biggest Driver</span>
            </div>
            <p className="text-base font-semibold text-white/90">{bd.source} <span style={{ color: v.accent }}>({bd.percentage}%)</span></p>
          </div>
        )}

        {node.suggested_action && (
          <div className="mb-6" data-testid="suggested-actions">
            <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-primary" /> Suggested Actions
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.12)" }}>
                <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-white/80">{node.suggested_action}</p>
              </div>
              {node.impact_potential && (
                <p className="text-xs text-white/40 ml-5">Impact: {node.impact_potential}</p>
              )}
            </div>
          </div>
        )}

        {node.real_world_implication && (
          <div className="mb-6 p-3 rounded-lg bg-white/[0.02] border border-white/5">
            <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Why This Matters</p>
            <p className="text-sm text-white/60 italic">{node.real_world_implication}</p>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">Sources of Impact</h3>
          <div className="space-y-2.5">
            {(node.breakdown || []).map((b, i) => {
              const isBiggest = bd && b.source === bd.source;
              return (
                <div key={`bd-${b.source}-${i}`} className="flex items-center gap-3">
                  <span className={`text-sm w-28 truncate ${isBiggest ? "text-white font-medium" : "text-white/60"}`}>{b.source}</span>
                  <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${b.percentage}%` }}
                      transition={{ delay: 0.2 + i * 0.1, duration: 0.6 }}
                      className="h-full rounded-full" style={{ background: v.accent, opacity: isBiggest ? 0.8 : 0.4 }} />
                  </div>
                  <span className={`text-xs w-8 text-right ${isBiggest ? "text-white/60 font-medium" : "text-white/30"}`}>{b.percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {crossImpacts.length > 0 && (
          <div className="mb-6" data-testid="cross-impacts">
            <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">Trade-off Signals</h3>
            <div className="space-y-2">
              {crossImpacts.map((ci, i) => (
                <div key={`ci-${ci.description?.slice(0,20)}-${i}`} className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 bg-amber-500/[0.04] border border-amber-500/10">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400/50 mt-0.5 shrink-0" />
                  <p className="text-white/50">{ci.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">Key Drivers</h3>
          <div className="flex flex-wrap gap-2">
            {(node.drivers || []).map((d, i) => (
              <span key={`drv-${d}-${i}`} className="px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/8 text-white/60">{d}</span>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-3">Data Confidence</h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/30">{CONF_ICON(node.confidence || 0)}</span>
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${(node.confidence || 0) * 100}%` }}
                transition={{ delay: 0.3, duration: 0.8 }} className="h-full rounded-full bg-primary" />
            </div>
            <span className="text-xs text-white/40 w-10 text-right">{Math.round((node.confidence || 0) * 100)}%</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
